import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, FlaskConical, Truck, Lock, BadgeCheck,
  FileCheck2, Boxes, MapPin, Bitcoin, CreditCard, Quote, ChevronRight,
} from "lucide-react";
import ProductCard from "../components/ProductCard";
import { PRODUCTS, CATEGORIES, LAB_IMAGES, TESTIMONIALS, SHIPPING_ETA } from "../mock";

const catIcons = {
  metabolic: FlaskConical, "growth-hormone": BadgeCheck, "tissue-repair": ShieldCheck,
  nootropic: Boxes, "skin-cellular": FileCheck2, mitochondrial: FlaskConical,
  melanocortin: BadgeCheck, "lab-supplies": Boxes,
};

const Home = () => {
  const featured = PRODUCTS.filter((p) => p.popularity >= 85).slice(0, 8);
  const cats = CATEGORIES.slice(1);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50 via-white to-white">
        <div className="absolute inset-0 fesx-grid-bg opacity-70" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fesx-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-100 text-blue-700 px-3.5 py-1.5 text-xs font-semibold tracking-wide">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                INDEPENDENT RESEARCH PLATFORM
              </span>
              <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-slate-900">
                Research-Grade<br />
                Peptides &amp; <span className="text-blue-600">Compounds</span>
              </h1>
              <p className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">
                Australia's research-first supplier delivering documented, batch-level HPLC
                results for scientific use. Same-day dispatch, published CoAs, express nationwide shipping.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/shop"
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3.5 transition-colors active:scale-[0.98]"
                >
                  Explore Research Peptides <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white hover:border-blue-400 hover:text-blue-700 text-slate-700 font-semibold px-6 py-3.5 transition-colors"
                >
                  Lab Results
                </Link>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
                {[
                  { k: "48+", v: "Compounds" },
                  { k: "99%+", v: "HPLC Purity" },
                  { k: "Same-Day", v: "Dispatch M–F" },
                ].map((s) => (
                  <div key={s.v}>
                    <div className="font-display font-extrabold text-2xl text-slate-900">{s.k}</div>
                    <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-blue-900/10 border border-white/60 fesx-fade-up">
                <img src={LAB_IMAGES.hero} alt="FesX research peptide vials" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/10 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-4 sm:left-6 bg-white rounded-2xl shadow-xl border border-slate-100 p-4 w-64 fesx-fade-up">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">GHK-Cu</span>
                  <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 rounded-full px-2 py-0.5">VERIFIED</span>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-sm font-bold text-slate-900">50 MG</div><div className="text-[10px] text-slate-400">Size</div></div>
                  <div><div className="text-sm font-bold text-blue-600">99.7%</div><div className="text-[10px] text-slate-400">Purity</div></div>
                  <div><div className="text-sm font-bold text-slate-900">HPLC</div><div className="text-[10px] text-slate-400">Tested</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS BAR */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Truck, t: "Same-Day Dispatch", s: "Mon–Fri from Australia" },
            { icon: Lock, t: "Secure Checkout", s: "Cards · Apple & Google Pay" },
            { icon: Bitcoin, t: "Crypto Saves 10%", s: "Auto-applied at checkout" },
            { icon: BadgeCheck, t: "Free Shipping $220+", s: "AusPost Express included" },
          ].map((b) => (
            <div key={b.t} className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <b.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-bold text-slate-900">{b.t}</div>
                <div className="text-xs text-slate-500">{b.s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600">Featured</span>
            <h2 className="mt-1 font-display font-extrabold text-3xl text-slate-900">Premium Peptides</h2>
            <p className="mt-2 text-slate-500">Lab-verified, batch by batch. Free express shipping over $220 AUD.</p>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-blue-600 font-semibold hover:gap-2.5 transition-all">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600">Catalogue</span>
            <h2 className="mt-1 font-display font-extrabold text-3xl text-slate-900">Eight Domains. One Standard.</h2>
            <p className="mt-2 text-slate-500">Every compound is grouped by research domain and passes the same 99%+ HPLC purity standard before release.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {cats.map((c) => {
              const Icon = catIcons[c.slug] || FlaskConical;
              const cnt = PRODUCTS.filter((p) => p.category === c.slug).length;
              return (
                <Link
                  key={c.slug}
                  to={`/shop?cat=${c.slug}`}
                  className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-900/5 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="text-xs font-bold text-slate-400">{String(cnt).padStart(2, "0")}</span>
                  </div>
                  <h3 className="mt-4 font-display font-bold text-slate-900 group-hover:text-blue-700 transition-colors">{c.name}</h3>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-600">
                    Browse <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden shadow-xl border border-slate-100">
            <img src={LAB_IMAGES.lab2} alt="Batch testing" className="w-full h-[400px] object-cover" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600">Transparency</span>
            <h2 className="mt-1 font-display font-extrabold text-3xl text-slate-900">Batch-Level Verification</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Unlike platforms that publish a single certificate per compound, FesX publishes independent
              laboratory results for <span className="font-semibold text-slate-800">every batch received</span>.
              Each batch is HPLC tested by a third-party lab, assigned a unique identifier, and published with its CoA.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {[
                { icon: FlaskConical, t: "Third-Party Tested", s: "Independent HPLC per batch" },
                { icon: FileCheck2, t: "Unique Batch IDs", s: "Full traceability" },
                { icon: BadgeCheck, t: "Published CoAs", s: "Linked to each listing" },
                { icon: ShieldCheck, t: "Purity Verified", s: "99%+ standard confirmed" },
              ].map((f) => (
                <div key={f.t} className="flex gap-3 rounded-xl border border-slate-200 p-4">
                  <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <f.icon className="h-4.5 w-4.5" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-900">{f.t}</div>
                    <div className="text-xs text-slate-500">{f.s}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SHIPPING MAP */}
      <section className="bg-blue-700 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-blue-200">Australia-Wide</span>
              <h2 className="mt-1 font-display font-extrabold text-3xl">From Our Lab To Every State.</h2>
              <p className="mt-3 text-blue-100 leading-relaxed">
                Same-day dispatch on Mon–Fri orders before 12pm AEST, then carried by AusPost Express across
                every state and territory. Fully tracked.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["AusPost Express", "Full tracking", "Secure packaging", "Discreet"].map((t) => (
                  <span key={t} className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium">{t}</span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {SHIPPING_ETA.map((s) => (
                <div key={s.code} className="rounded-xl bg-white/10 backdrop-blur p-4 flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-blue-200 shrink-0" />
                  <div>
                    <div className="text-sm font-bold">{s.code}</div>
                    <div className="text-[11px] text-blue-200">{s.eta}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs font-bold uppercase tracking-widest text-blue-600">Trusted by researchers</span>
          <h2 className="mt-1 font-display font-extrabold text-3xl text-slate-900">What Labs Say</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-lg transition-shadow">
              <Quote className="h-8 w-8 text-blue-200" />
              <p className="mt-3 text-slate-600 leading-relaxed">{t.text}</p>
              <div className="mt-5 pt-4 border-t border-slate-100">
                <div className="font-bold text-slate-900 text-sm">{t.name}</div>
                <div className="text-xs text-slate-500">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-blue-700 px-8 py-14 text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 fesx-grid-bg opacity-20" />
          <div className="relative">
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl">Choose A Compound. We'll Handle The Rest.</h2>
            <p className="mt-3 text-blue-100 max-w-2xl mx-auto">
              Independent batch testing, AusPost Express, transparent CoAs. Built for laboratories that need certainty.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/shop" className="inline-flex items-center gap-2 rounded-xl bg-white text-blue-700 font-bold px-6 py-3.5 hover:bg-blue-50 transition-colors">
                Browse the Catalogue <ArrowRight className="h-4 w-4" />
              </Link>
              <div className="inline-flex items-center gap-4 text-sm text-blue-100">
                <span className="flex items-center gap-1.5"><CreditCard className="h-4 w-4" /> Secure</span>
                <span className="flex items-center gap-1.5"><BadgeCheck className="h-4 w-4" /> 99%+ HPLC</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
